import React from 'react';
import { Box, Typography, Tabs, Tab, Paper, Table, TableHead, TableRow, TableCell, TableBody, Avatar, Tooltip } from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import { useNavigate, useLocation } from 'react-router-dom';
import { listUsersPublic, type AppUser } from '../services/userService';
import { getParticipants, type MeetingParticipantDto } from '../services/sttApi';
import projectService from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import { listNotes, getNote, NoteListItem, NoteDetails } from '../services/notesService';

const pad = (n: number) => String(n).padStart(2, '0');
const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NotesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  function md5(str: string): string {
    function L(k:any, d:any){return(k<<d)|(k>>>(32-d));}
    function K(G:any, k:any){var I,d,F,H,x;F=(G&2147483648);H=(k&2147483648);I=(G&1073741824);d=(k&1073741824);x=(G&1073741823)+(k&1073741823);if(I&d){return(x^2147483648^F^H);}if(I|d){if(x&1073741824){return(x^3221225472^F^H);}else{return(x^1073741824^F^H);}}else{return(x^F^H);}}
    function r(d:any,F:any,k:any){return(d&F)|((~d)&k);}
    function q(d:any,F:any,k:any){return(d&k)|(F&(~k));}
    function p(d:any,F:any,k:any){return(d^F^k);}
    function n(d:any,F:any,k:any){return(F^(d|(~k)));} // prettier-ignore
    function u(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(r(F,aa,Z),k),I));return K(L(G,H),F);}
    function f(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(q(F,aa,Z),k),I));return K(L(G,H),F);}
    function D(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(p(F,aa,Z),k),I));return K(L(G,H),F);}
    function t(G:any, F:any, aa:any, Z:any, k:any, H:any, I:any){G=K(G,K(K(n(F,aa,Z),k),I));return K(L(G,H),F);}
    function e(G:any){var Z;var F=G.length;var x=F+8;var k=(x-(x%64))/64;var I=(k+1)*16;var aa=Array(I-1);var d=0;var H=0;while(H<F){Z=(H-(H%4))/4;d=(H%4)*8;aa[Z]=(aa[Z]|(G.charCodeAt(H)<<d));H++;}Z=(H-(H%4))/4;d=(H%4)*8;aa[Z]=(aa[Z]|(128<<d));aa[I-2]=(F<<3);aa[I-1]=(F>>>29);return aa;}
    function B(x:any){var k="",F="",G,d;for(d=0;d<=3;d++){G=(x>>>(d*8))&255;F="0"+G.toString(16);k=k+F.substr(F.length-2,2);}return k;}
    function J(k:any){k=k.replace(/\r\n/g,"\n");var d="";for(var F=0;F<k.length;F++){var x=k.charCodeAt(F);if(x<128){d+=String.fromCharCode(x);}else{if((x>127)&&(x<2048)){d+=String.fromCharCode((x>>6)|192);d+=String.fromCharCode((x&63)|128);}else{d+=String.fromCharCode((x>>12)|224);d+=String.fromCharCode(((x>>6)&63)|128);d+=String.fromCharCode((x&63)|128);}}}return d;}
    var C:Array<number>;var m,g,h,y,v,w,E,S,O;var a=7,b=12,c=17,i=22;var l=5,j=9,o=14,_=20;var P=4,A=11,T=16,U=23;var Q=6,R=10,N=15,M=21;str=J(str);C=e(str);g=1732584193;h=4023233417;y=2562383102;v=271733878;for(m=0;m<C.length;m+=16){w=g;E=h;S=y;O=v;g=u(g,h,y,v,C[m+0],a,3614090360);v=u(v,g,h,y,C[m+1],b,3905402710);y=u(y,v,g,h,C[m+2],c,606105819);h=u(h,y,v,g,C[m+3],i,3250441966);g=u(g,h,y,v,C[m+4],a,4118548399);v=u(v,g,h,y,C[m+5],b,1200080426);y=u(y,v,g,h,C[m+6],c,2821735955);h=u(h,y,v,g,C[m+7],i,4249261313);g=u(g,h,y,v,C[m+8],a,1770035416);v=u(v,g,h,y,C[m+9],b,2336552879);y=u(y,v,g,h,C[m+10],c,4294925233);h=u(h,y,v,g,C[m+11],i,2304563134);g=u(g,h,y,v,C[m+12],a,1804603682);v=u(v,g,h,y,C[m+13],b,4254626195);y=u(y,v,g,h,C[m+14],c,2792965006);h=u(h,y,v,g,C[m+15],i,1236535329);g=f(g,h,y,v,C[m+1],l,4129170786);v=f(v,g,h,y,C[m+6],j,3225465664);y=f(y,v,g,h,C[m+11],o,643717713);h=f(h,y,v,g,C[m+0],_,3921069994);g=f(g,h,y,v,C[m+5],l,3593408605);v=f(v,g,h,y,C[m+10],j,38016083);y=f(y,v,g,h,C[m+15],o,3634488961);h=f(h,y,v,g,C[m+4],_,3889429448);g=f(g,h,y,v,C[m+9],l,568446438);v=f(v,g,h,y,C[m+14],j,3275163606);y=f(y,v,g,h,C[m+3],o,4107603335);h=f(h,y,v,g,C[m+8],_,1163531501);g=f(g,h,y,v,C[m+13],l,2850285829);v=f(v,g,h,y,C[m+2],j,4243563512);y=f(y,v,g,h,C[m+7],o,1735328473);h=f(h,y,v,g,C[m+12],_,2368359562);g=D(g,h,y,v,C[m+5],P,4294588738);v=D(v,g,h,y,C[m+8],A,2272392833);y=D(y,v,g,h,C[m+11],T,1839030562);h=D(h,y,v,g,C[m+14],U,4259657740);g=D(g,h,y,v,C[m+1],P,2763975236);v=D(v,g,h,y,C[m+4],A,1272893353);y=D(y,v,g,h,C[m+7],T,4139469664);h=D(h,y,v,g,C[m+10],U,3200236656);g=D(g,h,y,v,C[m+13],P,681279174);v=D(v,g,h,y,C[m+0],A,3936430074);y=D(y,v,g,h,C[m+3],T,3572445317);h=D(h,y,v,g,C[m+6],U,76029189);g=t(g,h,y,v,C[m+0],Q,3654602809);v=t(v,g,h,y,C[m+7],R,3873151461);y=t(y,v,g,h,C[m+14],N,530742520);h=t(h,y,v,g,C[m+5],M,3299628640);g=t(g,h,y,v,C[m+12],Q,4096336452);v=t(v,g,h,y,C[m+3],R,1126891415);y=t(y,v,g,h,C[m+10],N,2878612391);h=t(h,y,v,g,C[m+1],M,4237533241);g=K(g,w);h=K(h,E);y=K(y,S);v=K(v,O);}return (B(g)+B(h)+B(y)+B(v)).toLowerCase();
  }
  const gravatarUrl = (email: string, size = 64) => ''; // już nie używamy – inicjały jak w projektach
  const [tab, setTab] = React.useState<'notes'|'trans'>('notes');
  const [rows, setRows] = React.useState<NoteListItem[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<NoteDetails | null>(null);
  const [publicUsers, setPublicUsers] = React.useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);
  const [meetingParticipants, setMeetingParticipants] = React.useState<Record<string, MeetingParticipantDto[]>>({});
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listUsersPublic();
        if (!cancelled) setPublicUsers(Array.isArray(list) ? list : []);
      } catch { if (!cancelled) setPublicUsers([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await listNotes();
        if (!mounted) return;
        setRows(list);
        let targetId: string | null = null;
        try {
          const q = new URLSearchParams(location.search);
          targetId = q.get('note');
        } catch {}
        const firstId = targetId || (list[0]?.id || null);
        if (firstId) {
          setSelectedId(firstId);
          const det = await getNote(firstId);
          if (mounted) setSelected(det);
        }
        // load participants for all notes
        const ids = Array.from(new Set(list.map(n => n.meetingGoogleId).filter(Boolean))) as string[];
        const updates: Record<string, MeetingParticipantDto[]> = {};
        await Promise.all(ids.map(async (id) => {
          try {
            const l = await getParticipants(id);
            updates[id] = l;
          } catch {}
        }));
        if (mounted) setMeetingParticipants(prev => ({ ...prev, ...updates }));
      } catch {}
    })();
    return () => { mounted = false; };
  }, [location.search]);

  React.useEffect(() => {
    let mounted = true;
    if (!selectedId) { setSelected(null); return; }
    (async () => {
      try {
        const det = await getNote(selectedId);
        if (mounted) setSelected(det);
      } catch {}
    })();
    return () => { mounted = false; };
  }, [selectedId]);

  const onChangeTab = (_: any, value: 'notes'|'trans') => {
    setTab(value);
    if (value === 'trans') navigate('/app/notes/transcription');
  };
  const normalize = (s: string) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g,' ').trim();
  const findAvatar = (label: string): string | undefined => {
    const txt = String(label || '').trim();
    const emailMatch = txt.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0]?.toLowerCase();
    if (emailMatch) {
      const u = publicUsers.find(u => (u.email || '').toLowerCase() === emailMatch);
      if (u?.avatar_url) return u.avatar_url;
      if (user?.email && user.email.toLowerCase() === emailMatch) return user.avatar_url || undefined;
      return undefined;
    }
    const normTxt = normalize(txt);
    const u2 = publicUsers.find(u => normalize(u.display_name || '') === normTxt || normalize(u.display_name || '').includes(normTxt) || normTxt.includes(normalize(u.display_name || '')));
    if (u2?.avatar_url) return u2.avatar_url;
    const myNorm = normalize(user?.display_name || '');
    if (myNorm === normTxt || myNorm.includes(normTxt) || normTxt.includes(myNorm)) return user?.avatar_url || undefined;
    return undefined;
  };

  const firstChar = (label: string) => (String(label || '').trim() || '?').charAt(0).toLowerCase();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', mt: '64px' }}>
      {/* Nagłówek jak w projektach: biały pasek, tytuł, zakładki */}
      <Box sx={{ px: '24px', pt: 2, pb: 1, borderBottom: '1px solid #EEE', backgroundColor: '#FFF' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#2D2D32' }}>Notatki ze spotkań</Typography>
        <Tabs value={tab} onChange={onChangeTab} sx={{ minHeight: 36 }} TabIndicatorProps={{ sx: { height: 2 } }}>
          <Tab label="Notatki" value="notes" sx={{ minHeight: 36 }} />
          <Tab label="Transkrypcje" value="trans" sx={{ minHeight: 36 }} />
        </Tabs>
      </Box>

      {tab === 'notes' && (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, backgroundColor: '#f7f7fb', gap: '24px', p: '24px' }}>
          {/* Lista notatek – karta jak na stronie głównej */}
          <Paper elevation={0} sx={{ width: '45%', minWidth: 360, maxWidth: 720, border: '1px solid #eee', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D2D32' }}>Lista notatek</Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#2D2D32', bgcolor: '#F7F7FA' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2D2D32', bgcolor: '#F7F7FA' }}>Nazwa notatki</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2D2D32', bgcolor: '#F7F7FA' }}>Uczestnicy</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                  {rows.map((n) => {
                    const isSel = selectedId === n.id;
                    return (
                      <TableRow
                        key={n.id}
                        hover
                        selected={isSel}
                        onClick={() => setSelectedId(n.id)}
                        sx={{
                          cursor: 'pointer',
                          '&.Mui-selected': { backgroundColor: '#EEF2FF !important' },
                          '&:hover': { backgroundColor: '#F5F7FF' }
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{n.dateIso ? formatDateTime(n.dateIso) : ''}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{n.title}</TableCell>
                        <TableCell sx={{ color: '#5F6368' }}>
                          <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
                            {(() => {
                              let enriched = meetingParticipants[n.meetingGoogleId || ''] || [];
                              if (enriched.length > 0) {
                                const myEmail = (user?.email || '').toLowerCase();
                                const myName = (user?.display_name || '').toLowerCase();
                                const hasMe = enriched.some(u => (u.email && u.email.toLowerCase() === myEmail) || ((u.display_name || '').toLowerCase() === myName));
                                if (!hasMe) {
                                  enriched = [{ email: user?.email || null, display_name: user?.display_name || null, avatar_url: user?.avatar_url || null }, ...enriched];
                                }
                                return enriched.slice(0, 8).map((u, idx) => {
                                  const isMe = (u.email && u.email.toLowerCase() === myEmail) || ((u.display_name || '').toLowerCase() === myName);
                                  const src = isMe ? (user?.avatar_url || undefined) : (u.avatar_url || undefined);
                                  return (
                                    <Tooltip key={`${u.email || u.display_name || idx}`} title={u.display_name || u.email || ''}>
                                      <Avatar src={src} alt={u.display_name || u.email || ''} sx={{ width: 28, height: 28, fontSize: 12 }}>
                                        {firstChar(u.display_name || u.email || '')}
                                      </Avatar>
                                    </Tooltip>
                                  );
                                });
                              }
                              return n.participants.slice(0, 8).map((p, idx) => (
                                <Tooltip key={`${p}-${idx}`} title={p}>
                                  {(() => {
                                    const src = findAvatar(p);
                                    return (
                                      <Avatar src={src} alt={p} sx={{ width: 28, height: 28, fontSize: 12 }}>
                                        {firstChar(p)}
                                      </Avatar>
                                    );
                                  })()}
                                </Tooltip>
                              ));
                            })()}
                          </AvatarGroup>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>
          {/* Treść notatki – karta jak „sekcje” na HomePage */}
          <Paper elevation={0} sx={{ flex: 1, border: '1px solid #eee', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D2D32' }}>Treść notatki</Typography>
            </Box>
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              {selected ? (
                <>
                  {selected.dateIso ? (
                    <Typography variant="subtitle2" sx={{ color: '#5F6368', mb: 1 }}>
                      [{formatDateTime(selected.dateIso)} {selected.participants.join(', ')}]
                    </Typography>
                  ) : null}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#2D2D32' }}>{selected.title}</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selected.content}</Typography>
                </>
              ) : (
                <Typography color="text.secondary">Wybierz notatkę z listy po lewej.</Typography>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default NotesPage;


